import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, ButtonGroup, Card, Form, Modal, Nav, Table } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from "../contexts/LoadingContext";
import Header from "../layouts/Header";
import { isolate } from "../utils/bidi";
import { leaveReportedByLabel } from "../utils/leaveSource";
import { toISODate } from '../utils/thaiDate'

// เรียงและใช้สีชุดเดียวกับหน้าเช็คชื่อภาคสามัญ (teaching/CheckAttendance) — ครูคนเดียวกัน
// เช็คชื่อทั้งสองหน้าในวันเดียวกัน ถ้าลำดับปุ่มสลับกันจะกดผิดตำแหน่ง
const STATUS = [
  ["present", "มา", "success"],
  ["late", "สาย", "warning"],
  ["leave", "ลา", "info"],
  ["absent", "ขาด", "danger"],
];

const UPLOAD_BASE = (process.env.REACT_APP_API || "http://localhost:3002/api").replace(/\/api\/?$/, "").replace(/\/$/, "");

// รูปนักเรียน: วงกลมเล็กในตาราง ชี้ค้างเพื่อดูรูปใหญ่ คลิกเพื่อเปิดเต็ม
// (ชุดเดียวกับ teaching/CheckAttendance — ครูจำเด็กจากหน้าตาก่อนชื่อ)
const StudentAvatar = ({ photo, name, fill = false }) => {
  const [hovered, setHovered] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const initial = name ? name.trim().charAt(0) : "?";
  const src = photo ? `${UPLOAD_BASE}${photo.startsWith("/") ? photo : `/${photo}`}` : null;
  const circleStyle = { width: 28, height: 28, borderRadius: "50%", flexShrink: 0 };

  // ในการ์ด รูปกินความสูงทั้งแถบซ้าย — ไม่ต้องชี้ค้างเพราะนิ้วบนมือถือไม่มี hover
  if (fill) {
    return (
      <>
        <div
          style={{ width: "100%", height: "100%", cursor: src ? "pointer" : "default" }}
          onClick={() => src && setShowModal(true)}
        >
          {src
            ? <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : <div style={{ width: "100%", height: "100%", backgroundColor: "#adb5bd", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 600 }}>{initial}</div>
          }
        </div>
        {src && (
          <Modal show={showModal} onHide={() => setShowModal(false)} centered size="sm">
            <Modal.Body className="p-3 text-center">
              <img src={src} alt={name} style={{ maxWidth: "100%", maxHeight: "70vh", borderRadius: 8 }} />
              {name && <div className="mt-2 fw-semibold small">{name}</div>}
            </Modal.Body>
          </Modal>
        )}
      </>
    );
  }

  return (
    <>
      <span
        style={{ position: "relative", display: "inline-block", lineHeight: 0 }}
        onMouseEnter={() => src && setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => src && setShowModal(true)}
      >
        {src
          ? <img src={src} alt="" style={{ ...circleStyle, objectFit: "cover", cursor: "pointer" }} />
          : <span style={{ ...circleStyle, backgroundColor: "#adb5bd", color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600 }}>{initial}</span>
        }
        {src && hovered && (
          <div style={{ position: "absolute", left: 34, top: "50%", transform: "translateY(-50%)", zIndex: 1050, background: "#fff", borderRadius: 8, boxShadow: "0 4px 20px rgba(0,0,0,0.25)", padding: 4 }}>
            <img src={src} alt={name} style={{ width: 120, height: 120, objectFit: "cover", borderRadius: 6, display: "block" }} />
          </div>
        )}
      </span>
      {src && (
        <Modal show={showModal} onHide={() => setShowModal(false)} centered size="sm">
          <Modal.Body className="p-3 text-center">
            <img src={src} alt={name} style={{ maxWidth: "100%", maxHeight: "70vh", borderRadius: 8 }} />
            {name && <div className="mt-2 fw-semibold small">{name}</div>}
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

// c_class เก็บชื่อเต็ม ("ชั้นมัธยมศึกษาปีที่ 2") ย่อเป็น "ม.2/4" เหมือนที่อื่นในระบบ
// ไม่งั้นคอลัมน์ชั้นเรียนกินความกว้างจนปุ่มสถานะถูกบีบตกบรรทัด
const generalClassLabel = (row) => (row.c_class ? `${String(row.c_class).replace("ชั้นมัธยมศึกษาปีที่ ", "ม.")}/${row.r_no ?? ""}` : "-");
// เลขห้องศาสนาเป็นอักษรอาหรับ ต้องห่อ isolate ไม่ให้ bidi ลากเลขชั้นไปสลับที่
const islamicClassLabel = (row) => (row.islamic_class ? `${row.islamic_class}/${isolate(String(row.islamic_room_no || "").trim())}` : "-");

// ตรวจจับหน้าจอมือถือ (max-width: 768px) — ชุดเดียวกับ teaching/CheckAttendance
const useIsMobile = (query = "(max-width: 768px)") => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" && window.matchMedia(query).matches
  );
  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [query]);
  return isMobile;
};

// ป้ายกำกับเมื่อนักเรียนลาไว้ล่วงหน้า — ครูประจำชั้น/ฝ่ายปกครอง/หัวหน้าวิชาการแจ้งลา
// หรือมีใบอนุญาตกลับบ้านที่ครอบวันที่ของรอบนี้ (ชุดเดียวกับ teaching/CheckAttendance)
const LeaveBadge = ({ leave }) => {
  if (!leave) return null;
  if (leave.source === "home_leave") {
    // returned_at = เวลาที่ยามกดว่ากลับเข้าโรงเรียนแล้ว บอกครูว่าเด็กคนนี้กลับมาแล้ว
    // จะได้ไม่งงว่าทำไมมีใบกลับบ้านแต่ระบบไม่ตั้ง "ลา" ให้
    const back = leave.returned_at ? ` · กลับเข้า ${leave.returned_at.slice(11, 16)} น.` : "";
    return (
      <span className={`badge ${leave.is_active ? "bg-info" : "bg-secondary"} ms-2`} title={leave.leave_note || ""}>
        <i className="ri-home-heart-line me-1"></i>กลับบ้าน{leave.leave_note ? ` (${leave.leave_note})` : ""}{back}
      </span>
    );
  }
  const label = leave.leave_type === "sick" ? "ป่วย" : "กิจ";
  return (
    <span className="badge bg-info ms-2" title={leave.leave_note || ""}>
      <i className="ri-calendar-close-line me-1"></i>{leaveReportedByLabel(leave.source)} ({label})
    </span>
  );
};

const today = () => toISODate(new Date());

export default function ClubWorkspace() {
  const { clubYearId } = useParams();
  const { setLoading } = useLoading();
  const [club, setClub] = useState(null);
  const [members, setMembers] = useState([]);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState("attendance");
  const [semester, setSemester] = useState("1");
  const [sessionDate, setSessionDate] = useState(today());
  const [openRounds, setOpenRounds] = useState([]);
  const [sessionRoundId, setSessionRoundId] = useState("");
  const [topic, setTopic] = useState("");
  const [attendance, setAttendance] = useState({});
  const [assessment, setAssessment] = useState({});
  const [notes, setNotes] = useState({});
  const [editSession, setEditSession] = useState(null);
  const [studentQuery, setStudentQuery] = useState("");
  const [studentResults, setStudentResults] = useState([]);
  const [leaves, setLeaves] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [myRes, memberRes, historyRes, roundRes] = await Promise.all([
        axiosInstance.get("/api/clubs/my-clubs"),
        axiosInstance.get(`/api/clubs/offerings/${clubYearId}/members`),
        axiosInstance.get(`/api/clubs/offerings/${clubYearId}/attendance`),
        axiosInstance.get(`/api/clubs/offerings/${clubYearId}/open-session-rounds`),
      ]);
      setClub(myRes.data.find(c => String(c.club_year_id) === String(clubYearId)) || null);
      const active = memberRes.data.filter(m => m.enrollment_status === "active");
      setMembers(active);
      setHistory(historyRes.data);
      setOpenRounds(roundRes.data);
      setAttendance(Object.fromEntries(active.map(m => [m.club_enrollment_id, "present"])));
      setAssessment(Object.fromEntries(active.map(m => [
        m.club_enrollment_id,
        m[`assessment_sem${semester}`] || "pending"
      ])));
    } catch (error) {
      Swal.fire("ผิดพลาด", error.response?.data?.message || "โหลดข้อมูลไม่สำเร็จ", "error");
    } finally { setLoading(false); }
  }, [clubYearId, semester, setLoading]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setAssessment(Object.fromEntries(members.map(m => [
      m.club_enrollment_id, m[`assessment_sem${semester}`] || "pending"
    ])));
  }, [semester, members]);

  // การลาที่ครอบรอบที่เลือก — วันที่กับคาบอยู่ที่ฝั่ง server ส่งไปแค่ว่ารอบไหน
  // โหลดไม่สำเร็จก็ไม่ขวางการเช็คชื่อ แค่ไม่มีป้ายและไม่ตั้ง "ลา" ให้
  useEffect(() => {
    if (!sessionRoundId) { setLeaves({}); return undefined; }
    let alive = true;
    axiosInstance.get(`/api/clubs/offerings/${clubYearId}/session-rounds/${sessionRoundId}/leaves`)
      .then(({ data }) => {
        if (alive) setLeaves(Object.fromEntries((data || []).map(l => [l.club_enrollment_id, l])));
      })
      .catch(() => { if (alive) setLeaves({}); });
    return () => { alive = false; };
  }, [clubYearId, sessionRoundId]);

  // ตั้ง "ลา" ให้คนที่แจ้งลาไว้อัตโนมัติ — ดึงกลับทุกครั้งที่รายชื่อหรือรายการลาเปลี่ยน
  // (เปลี่ยนรอบ / โหลดใหม่หลังบันทึก) แต่ไม่ทับระหว่างที่ครูกำลังติ๊ก เพราะไม่ได้ผูกกับ
  // attendance คนที่ยามกดว่ากลับเข้าโรงเรียนแล้วไม่นับ — ป้ายยังขึ้นให้ครูเห็น
  useEffect(() => {
    if (!members.length) return;
    setAttendance(prev => {
      const next = { ...prev };
      members.forEach(m => {
        if (leaves[m.club_enrollment_id]?.is_active) next[m.club_enrollment_id] = "leave";
      });
      return next;
    });
  }, [members, leaves]);

  const attendanceTotals = useMemo(() => members.reduce((totals, m) => {
    totals.present += Number(m.presents || 0);
    totals.absent += Number(m.absents || 0);
    totals.late += Number(m.lates || 0);
    totals.leave += Number(m.leaves || 0);
    return totals;
  }, { present: 0, absent: 0, late: 0, leave: 0 }), [members]);

  const saveAttendance = async (event) => {
    event.preventDefault();
    try {
      await axiosInstance.post(`/api/clubs/offerings/${clubYearId}/attendance`, {
        club_session_round_id: Number(sessionRoundId), session_topic: topic,
        records: members.map(m => ({
          club_enrollment_id: m.club_enrollment_id,
          attendance_status: attendance[m.club_enrollment_id] || "present"
        }))
      });
      setTopic("");
      await load();
      Swal.fire({ icon: "success", title: "บันทึกเช็คชื่อแล้ว", timer: 1200, showConfirmButton: false });
    } catch (error) {
      const code = error.response?.data?.code;
      const text = code === "SESSION_ROUND_NOT_OPEN" ? "ฝ่ายการเรียนการสอนยังไม่ได้เปิดรอบนี้"
        : code === "ATTENDANCE_EXISTS" ? "มีการเช็คชื่อชุมนุมในรอบนี้แล้ว"
        : error.response?.data?.message || "บันทึกไม่สำเร็จ";
      Swal.fire("บันทึกไม่ได้", text, "error");
    }
  };

  const searchStudents = async (event) => {
    event?.preventDefault();
    if (studentQuery.trim().length < 2) return;
    try {
      const { data } = await axiosInstance.get(`/api/clubs/offerings/${clubYearId}/students/search`, { params: { q: studentQuery.trim() } });
      setStudentResults(data);
    } catch (error) { Swal.fire("ค้นหาไม่สำเร็จ", error.response?.data?.message || "เกิดข้อผิดพลาด", "error"); }
  };

  const enrollStudent = async (student) => {
    try {
      await axiosInstance.post(`/api/clubs/offerings/${clubYearId}/enrollments`, { st_id: student.st_id });
      setStudentQuery(""); setStudentResults([]);
      await load();
      Swal.fire({ icon: "success", title: "รับนักเรียนเข้าชุมนุมแล้ว", timer: 1200, showConfirmButton: false });
    } catch (error) { Swal.fire("รับสมัครไม่สำเร็จ", error.response?.data?.message || "เกิดข้อผิดพลาด", "error"); }
  };
  const saveAssessments = async () => {
    try {
      await axiosInstance.put(`/api/clubs/offerings/${clubYearId}/assessments`, {
        semester,
        records: members.map(m => ({
          club_enrollment_id: m.club_enrollment_id,
          assessment_result: assessment[m.club_enrollment_id] || "pending",
          assessment_note: notes[m.club_enrollment_id] || ""
        }))
      });
      await load();
      Swal.fire({ icon: "success", title: "บันทึกผลประเมินแล้ว", timer: 1200, showConfirmButton: false });
    } catch (error) { Swal.fire("บันทึกไม่สำเร็จ", error.response?.data?.message || "เกิดข้อผิดพลาด", "error"); }
  };

  const openSession = async (sessionId) => {
    try {
      const { data } = await axiosInstance.get(`/api/clubs/attendance/${sessionId}`);
      setEditSession({
        ...data[0],
        records: data.map(row => ({
          club_enrollment_id: row.club_enrollment_id,
          st_idnumber: row.st_idnumber,
          st_name: row.st_name,
          st_photo: row.st_photo,
          c_class: row.c_class,
          r_no: row.r_no,
          islamic_class: row.islamic_class,
          islamic_room_no: row.islamic_room_no,
          attendance_status: row.attendance_status
        }))
      });
    } catch (error) { Swal.fire("ผิดพลาด", "โหลดรายการเช็คชื่อไม่สำเร็จ", "error"); }
  };

  const updateSession = async () => {
    try {
      await axiosInstance.put(`/api/clubs/attendance/${editSession.club_session_id}`, {
        session_topic: editSession.session_topic, records: editSession.records
      });
      setEditSession(null);
      await load();
      Swal.fire({ icon: "success", title: "แก้ไขแล้ว", timer: 1000, showConfirmButton: false });
    } catch (error) { Swal.fire("แก้ไขไม่สำเร็จ", error.response?.data?.message || "เกิดข้อผิดพลาด", "error"); }
  };

  return (
    <>
      <Header />
      <main className="main main-app p-3 p-lg-4">
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <div className="mb-2"><Link to="/clubs/my" className="text-decoration-none">← ชุมนุมของฉัน</Link></div>
          <h4 className="main-title mb-1">{club?.club_name || "ชุมนุม"}</h4>
          <div className="text-secondary">สมาชิก {members.length} คน · {club?.advisor_role === "lead" ? "ครูผู้ดูแลหลัก" : "ครูผู้ช่วย"}</div>
        </div>
        <Badge bg={club?.club_gender === "m" ? "primary" : "danger"}>{club?.club_gender === "m" ? "ชาย" : "หญิง"}</Badge>
      </div>

      <div className="d-flex flex-wrap gap-2 mb-3">
        {STATUS.map(([key, label, variant]) => <Badge key={key} bg={variant} className="p-2">{label}สะสม {attendanceTotals[key]}</Badge>)}
      </div>

      <Nav variant="tabs" activeKey={tab} onSelect={setTab} className="mb-3">
        <Nav.Item><Nav.Link eventKey="attendance">เช็คชื่อ</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="assessment">ประเมินผล</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="history">ประวัติเช็คชื่อ</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="members">สมาชิก</Nav.Link></Nav.Item>
      </Nav>

      {tab === "attendance" && (
        <Card className="card-one">
          <Form onSubmit={saveAttendance}>
            <Card.Header className="d-flex flex-wrap gap-2 align-items-center">
              <Card.Title as="h6" className="mb-0 me-auto">เช็คชื่อชุมนุม</Card.Title>
              <Form.Select size="sm" required value={sessionRoundId} onChange={e => { const round = openRounds.find(r => String(r.club_session_round_id) === e.target.value); setSessionRoundId(e.target.value); if (round) setSemester(String(round.semester)); }} style={{ width: 260 }}><option value="">เลือกรอบที่ฝ่ายการเรียนการสอนเปิด</option>{openRounds.map(r => <option key={r.club_session_round_id} value={r.club_session_round_id}>{String(r.session_date).slice(0, 10)} · ภาค {r.semester} · คาบ {r.period_order}</option>)}</Form.Select>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3"><Form.Label>หัวข้อกิจกรรม</Form.Label><Form.Control required value={topic} onChange={e => setTopic(e.target.value)} placeholder="ระบุหัวข้อกิจกรรมของวันนี้" /></Form.Group>
              <MemberStatusTable members={members} values={attendance} leaves={leaves} onChange={(id, value) => setAttendance(prev => ({ ...prev, [id]: value }))} />
              <div className="text-end"><Button type="submit" disabled={!members.length}>บันทึกเช็คชื่อ</Button></div>
            </Card.Body>
          </Form>
        </Card>
      )}

      {tab === "assessment" && (
        <Card className="card-one">
          <Card.Header className="d-flex flex-wrap gap-2 align-items-center">
            <Card.Title as="h6" className="mb-0 me-auto">ประเมินผ่าน / ไม่ผ่าน</Card.Title>
            <Form.Select size="sm" value={semester} onChange={e => setSemester(e.target.value)} style={{ width: 140 }}><option value="1">ภาคเรียน 1</option><option value="2">ภาคเรียน 2</option></Form.Select>
            <Button size="sm" variant="outline-success" onClick={() => setAssessment(Object.fromEntries(members.map(m => [m.club_enrollment_id, "pass"])))}>ผ่านทั้งหมด</Button>
          </Card.Header>
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0 align-middle">
              <thead><tr><th>รหัส</th><th>ชื่อ</th><th>สรุปเข้าเรียน</th><th>ผล</th><th>หมายเหตุ</th></tr></thead>
              <tbody>{members.map(m => <tr key={m.club_enrollment_id}><td>{m.st_idnumber}</td><td>{m.st_name}</td><td>มา {m.presents || 0} · ขาด {m.absents || 0} · สาย {m.lates || 0} · ลา {m.leaves || 0}</td><td><Form.Select size="sm" value={assessment[m.club_enrollment_id] || "pending"} onChange={e => setAssessment(prev => ({ ...prev, [m.club_enrollment_id]: e.target.value }))}><option value="pending">รอประเมิน</option><option value="pass">ผ่าน</option><option value="fail">ไม่ผ่าน</option></Form.Select></td><td><Form.Control size="sm" value={notes[m.club_enrollment_id] || ""} onChange={e => setNotes(prev => ({ ...prev, [m.club_enrollment_id]: e.target.value }))} /></td></tr>)}</tbody>
            </Table>
            <div className="text-end p-3"><Button onClick={saveAssessments}>บันทึกผลประเมิน</Button></div>
          </Card.Body>
        </Card>
      )}

      {tab === "history" && (
        <Card className="card-one"><Card.Body className="p-0"><Table responsive hover className="mb-0"><thead><tr><th>วันที่</th><th>ภาคเรียน</th><th>หัวข้อ</th><th>ผู้เช็ค</th><th>มา/ขาด/สาย/ลา</th><th /></tr></thead><tbody>{history.map(row => <tr key={row.club_session_id}><td>{String(row.session_date).slice(0, 10)}</td><td>{row.semester}</td><td>{row.session_topic}</td><td>{row.checked_by_name}</td><td>{row.presents || 0}/{row.absents || 0}/{row.lates || 0}/{row.leaves || 0}</td><td><Button size="sm" variant="outline-primary" onClick={() => openSession(row.club_session_id)}>แก้ไข</Button></td></tr>)}</tbody></Table></Card.Body></Card>
      )}

      {tab === "members" && (
        <Card className="card-one">
          <Card.Header><Card.Title as="h6" className="mb-0">สมาชิกชุมนุม</Card.Title></Card.Header>
          <Card.Body>
            <Form onSubmit={searchStudents} className="d-flex gap-2 mb-3">
              <Form.Control placeholder="ค้นหาด้วยเลขประจำตัวหรือชื่อ (อย่างน้อย 2 ตัวอักษร)" value={studentQuery} onChange={e => setStudentQuery(e.target.value)} />
              <Button type="submit">ค้นหา</Button>
            </Form>
            {!!studentResults.length && <div className="border rounded mb-3">{studentResults.map(student => <div key={student.st_id} className="d-flex justify-content-between align-items-center gap-2 p-2 border-bottom"><span>{student.st_idnumber} {student.st_name} ({student.c_class}/{student.r_no}) {student.current_club_name && <Badge bg="warning" text="dark">อยู่ชุมนุม {student.current_club_name}</Badge>}</span><Button size="sm" disabled={!!student.current_club_year_id} onClick={() => enrollStudent(student)}>รับสมัคร</Button></div>)}</div>}
          </Card.Body>
          <Table responsive hover className="mb-0"><thead><tr><th>รหัส</th><th>ชื่อ</th><th>ห้อง</th><th>สมัครโดย</th><th>ผลภาค 1</th><th>ผลภาค 2</th></tr></thead><tbody>{members.map(m => <tr key={m.club_enrollment_id}><td>{m.st_idnumber}</td><td>{m.st_name}</td><td>{m.c_class}/{m.r_no}</td><td>{m.enrollment_source === "portal" ? "นักเรียน" : "ผู้ดูแล"}</td><td>{resultLabel(m.assessment_sem1)}</td><td>{resultLabel(m.assessment_sem2)}</td></tr>)}</tbody></Table>
        </Card>
      )}
      <Modal show={!!editSession} onHide={() => setEditSession(null)} size="lg">
        <Modal.Header closeButton><Modal.Title>แก้ไขเช็คชื่อ {editSession?.session_date && String(editSession.session_date).slice(0, 10)}</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3"><Form.Label>หัวข้อ</Form.Label><Form.Control value={editSession?.session_topic || ""} onChange={e => setEditSession(prev => ({ ...prev, session_topic: e.target.value }))} /></Form.Group>
          <MemberStatusTable members={editSession?.records || []} values={Object.fromEntries((editSession?.records || []).map(r => [r.club_enrollment_id, r.attendance_status]))} onChange={(id, value) => setEditSession(prev => ({ ...prev, records: prev.records.map(r => r.club_enrollment_id === id ? { ...r, attendance_status: value } : r) }))} />
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setEditSession(null)}>ยกเลิก</Button><Button onClick={updateSession}>บันทึกการแก้ไข</Button></Modal.Footer>
      </Modal>
      </main>
    </>
  );
}

// ปุ่มเลือกสถานะ — เลือกได้อย่างเดียวต่อคน ปุ่มที่เลือกเป็นสีทึบเต็มปุ่ม ที่เหลือเป็นเส้นขอบ
// ใช้ปุ่มธรรมดาไม่ใช่ ToggleButton เพราะ input.btn-check ที่ถูกติ๊กจะถูก Bootstrap ทาสี
// active ซึ่งเข้มกว่าสีเต็มของปุ่ม ทำให้สีไม่ตรงกับหน้าเช็คชื่อภาคสามัญที่วางข้างกัน
function AttendanceButtons({ enrollmentId, value, onChange }) {
  const current = value || "present";
  return (
    <ButtonGroup>
      {STATUS.map(([key, label, variant]) => (
        <Button
          key={key}
          size="sm"
          variant={current === key ? variant : `outline-${variant}`}
          onClick={() => onChange(enrollmentId, key)}
        >
          {label}
        </Button>
      ))}
    </ButtonGroup>
  );
}

// รายชื่อสำหรับเช็คชื่อ — ตารางบนจอใหญ่ การ์ดบนมือถือ (ตารางต้องเลื่อนซ้ายขวาจึงจะ
// เห็นปุ่มสถานะ ซึ่งเป็นสิ่งเดียวที่ครูต้องกด) ผู้ใช้สลับเองได้ และการเลือกของผู้ใช้
// ไม่ถูกเขียนทับเมื่อหมุนจอ — เหมือน teaching/CheckAttendance
function MemberStatusTable({ members, values, leaves = {}, onChange }) {
  const isMobile = useIsMobile();
  const [viewMode, setViewMode] = useState(null);
  const effectiveView = viewMode ?? (isMobile ? "card" : "table");

  return (
    <>
      <div className="d-flex justify-content-end mb-2">
        <ButtonGroup size="sm">
          <Button variant={effectiveView === "card" ? "primary" : "outline-primary"} onClick={() => setViewMode("card")} title="มุมมองการ์ด">
            <i className="ri-layout-grid-line"></i> การ์ด
          </Button>
          <Button variant={effectiveView === "table" ? "primary" : "outline-primary"} onClick={() => setViewMode("table")} title="มุมมองตาราง">
            <i className="ri-table-line"></i> ตาราง
          </Button>
        </ButtonGroup>
      </div>

      {effectiveView === "table" ? (
        <Table responsive hover className="align-middle">
          <thead>
            <tr>
              <th className="text-center" style={{ width: "50px" }}>เลขที่</th>
              <th className="text-center" style={{ width: "100px" }}>เลขประจำตัว</th>
              <th>ชื่อ-สกุล</th>
              <th className="text-center" style={{ width: "100px" }}>ชั้นสามัญ</th>
              <th className="text-center" style={{ width: "100px" }}>ชั้นศาสนา</th>
              <th className="text-center" style={{ width: "210px" }}>สถานะการเข้าเรียน</th>
            </tr>
          </thead>
          <tbody>
            {members.length ? members.map((m, index) => (
              <tr key={m.club_enrollment_id}>
                <th scope="row" className="text-center">{index + 1}</th>
                <td className="text-center">{m.st_idnumber}</td>
                <td>
                  <div className="d-flex align-items-center gap-2">
                    <StudentAvatar photo={m.st_photo} name={m.st_name} />
                    <span>{m.st_name}<LeaveBadge leave={leaves[m.club_enrollment_id]} /></span>
                  </div>
                </td>
                <td className="text-center">{generalClassLabel(m)}</td>
                <td className="text-center">{islamicClassLabel(m)}</td>
                <td className="text-center">
                  <AttendanceButtons enrollmentId={m.club_enrollment_id} value={values[m.club_enrollment_id]} onChange={onChange} />
                </td>
              </tr>
            )) : (
              <tr><td colSpan={6} className="text-center text-secondary">ไม่พบข้อมูล</td></tr>
            )}
          </tbody>
        </Table>
      ) : (
        <div className="d-flex flex-column gap-2 mb-3">
          {members.length ? members.map((m, index) => (
            <Card key={m.club_enrollment_id} className="border">
              <Card.Body className="p-0" style={{ minHeight: 76 }}>
                <div className="d-flex" style={{ minHeight: 76 }}>
                  {/* รูปใหญ่ชิดซ้าย */}
                  <div style={{ width: 64, flexShrink: 0, overflow: "hidden", borderRadius: "calc(0.375rem - 1px) 0 0 calc(0.375rem - 1px)" }}>
                    <StudentAvatar photo={m.st_photo} name={m.st_name} fill />
                  </div>
                  {/* ข้อมูล + ปุ่ม */}
                  <div className="flex-grow-1 px-2 py-2 d-flex flex-column justify-content-between">
                    <div className="d-flex align-items-start gap-2">
                      <span className="badge bg-secondary flex-shrink-0 mt-1">{index + 1}</span>
                      <div className="flex-grow-1 text-end">
                        <div className="fw-semibold" style={{ fontSize: "0.875rem" }}>{m.st_name}</div>
                        <small className="text-muted">เลขประจำตัว {m.st_idnumber}</small>
                        <div><small className="text-muted">สามัญ {generalClassLabel(m)} · ศาสนา {islamicClassLabel(m)}</small></div>
                        {leaves[m.club_enrollment_id] && <div className="mt-1"><LeaveBadge leave={leaves[m.club_enrollment_id]} /></div>}
                      </div>
                    </div>
                    <div className="d-flex justify-content-end mt-2">
                      <AttendanceButtons enrollmentId={m.club_enrollment_id} value={values[m.club_enrollment_id]} onChange={onChange} />
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          )) : (
            <div className="text-center text-secondary py-3">ไม่พบข้อมูล</div>
          )}
        </div>
      )}
    </>
  );
}

function resultLabel(value) {
  if (value === "pass") return "ผ่าน";
  if (value === "fail") return "ไม่ผ่าน";
  return "รอประเมิน";
}
